import { isStringArray, JsonObject } from '@se/cp.common';
import { ColDef, ColumnState } from 'ag-grid-community';
import { cloneDeep, difference, filter, forEach, groupBy, sortBy } from 'lodash-es';
import moment, { Moment } from 'moment';
import { BehaviorSubject } from 'rxjs';
import { ToolbarButtonData } from '../se-toolbar/se-buttons-toolbar/types';
import { RestControllerName } from '../services/angularjs/estensa-collection.service';
import { isDateWithinRange, rangesOverlap } from './helpers';
import { SearchFormType } from './search-page-type.enum';

export enum SearchParamType {
    string = 'string',
    boolean = 'boolean',
    date = 'date',
    time = 'time',
    number = 'number',
    select = 'select',
    multiSelect = 'multiSelect',
    filterSelect = 'filterSelect',
    filterMultiSelect = 'filterMultiSelect',
    fromTo = 'fromTo',
}

export enum EstensaSettingsType {
    user = 'user',
    site = 'site',
}

export enum GridSelectionModel {
    multiple = 'multiple',
    single = 'single',
}

export enum PostLoadingSelection {
    nothing = 'nothing', // Nessuna selezione
    firstItem = 'firstItem', // Il primo item
}

export enum WorklistSearchType {
    All = 0,
    Reporting = 1,
    Reported = 2,
    Approved = 3,
}

export interface ISearchParamLayout {
    row: number;
    column: number;
    stretch?: boolean;
    autofocus?: boolean;
    disabled?: boolean; // UI attiva o no (abilita
    // -------------------------------------------------------------------------
    // ADDED: colspan property for double surname feature
    // When colspan > 1, the field spans multiple columns in the layout grid
    // Example: Name field with colspan=2 spans full width when double surname is enabled
    // -------------------------------------------------------------------------
    colspan?: number;
}

export interface ISearchParamItemsSource {
    form: SearchFormType;
    controllerName: RestControllerName;
    methodName: string;
    displayProperty?: string;
    dataLoading?: boolean;
    data?: JsonObject[];
    originalData?: JsonObject[];
    enableTooltip?: boolean;
    disableResetButton?: boolean;
    sortCollectionDataInfo: ISearchCollectionSortingInfo;
    refSearchParamPropertyNames?: string[];
    foreingKeys?: string[];
}

export interface ISearchCollectionSortingInfo {
    field: string;
}

export interface FromToLabel {
    from: string;
    to: string;
    label?: string; // se valorizzata viene messa in un tooltip
}

export interface UserSearchParam {
    layout: ISearchParamLayout;
    property: ISearchParamProperty;
}

export interface SiteSearchParam {
    active?: boolean; // Filtro usato o no (compare scompare dall'interfaccia)
    labelTranslationKey: FromToLabel | string;
    property: ISearchParamProperty;
}

export interface SearchParam {
    /*extends UserSearchParam, SiteSearchParam*/
    active?: boolean; // Filtro usato o no (compare scompare dall'interfaccia)
    labelTranslationKey?: FromToLabel | string;
    layout?: ISearchParamLayout;
    property: ISearchParamProperty;
    canSave?: boolean;
    clearOnBack?: boolean;
    autoOpen?: boolean;
}

export interface ISearchParamProperty {
    name: string; // ID
    value?: any;
    defaultValue?: any;
    type?: SearchParamType;
    itemsSourceInfo?: ISearchParamItemsSource;
}

/*
export interface ISearchParamProperty extends ISiteSearchParamProperty, IUserSearchParamProperty {
}

export interface ISiteSearchParamProperty {
    name: string; // ID
    type: SearchParamType;
    itemsSourceInfo?: ISearchParamItemsSource;
}

export interface IUserSearchParamProperty {
    name: string; // ID
    value: any;
    defaultValue?: any;
}
*/

export interface SearchTypeInfo {
    id: number;
    labelTranslationKey: string;
    isDefaultSearch?: boolean;
}

export interface GridState {
    colState: any;
}

export class SearchParams {
    params: Record<string, any>;
    layouts: Record<string, ISearchParamLayout>;

    private dataValues: Record<string, any> = {};
    private dataType: Record<string, SearchParamType> = {};
    private dataLayout: Record<string, ISearchParamLayout> = {};

    private _refParams: Record<string, string[]> = {};

    private _dataItemsSource: Record<string, ISearchParamItemsSource> = {};
    get dataItemsSource(): Record<string, ISearchParamItemsSource> {
        return this._dataItemsSource;
    }

    private _searchParamsGroupedByRow: Record<string, SearchParam[]> = {};

    sourceLoadingCompleted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get searchParamsFullGroupedByRow(): Array<SearchParam[]> {
        // Step 1. Get all the object keys.
        let keys = Object.keys(this._searchParamsGroupedByRow);
        const max = Math.max(...keys.map((key) => parseInt(key)));
        // Step 2. Create an empty array.
        let array: Array<SearchParam[]> = new Array(max + 1);
        array = array.map((_) => []);
        // Step 3. Iterate throw all keys.
        for (let key of keys) {
            const _key = parseInt(key);
            array[_key] = this._searchParamsGroupedByRow[key];
        }

        return array;
    }

    get searchParamsGroupedByRow(): Array<SearchParam[]> {
        // Step 1. Get all the object keys.
        let keys = Object.keys(this._searchParamsGroupedByRow);
        // Step 2. Create an empty array.
        let array: Array<SearchParam[]> = [];
        // Step 3. Iterate throw all keys.
        for (let key of keys) {
            array.push(this._searchParamsGroupedByRow[key]);
        }

        return array;
    }

    isAllDataLoadingEnded(): boolean {
        return Object.values(this.dataItemsSource).reduce((prev, value) => prev && !value.dataLoading, true);
    }

    endAllDataLoading() {
        this._searchParamList.forEach((param) => {
            if (!!param.property.itemsSourceInfo?.dataLoading) param.property.itemsSourceInfo.dataLoading = false;
        });
    }

    endDataLoading(name: string) {
        if (!!this.dataItemsSource[name]?.dataLoading) this.dataItemsSource[name].dataLoading = false;
        if (this.isAllDataLoadingEnded()) {
            this.filterAllComboItems();
            this.removeMissingDefaultValues();
            this.sourceLoadingCompleted$.next(true);
        }
    }

    addData(name: string, data: JsonObject[]) {
        this._dataItemsSource[name].data = data;
        this._dataItemsSource[name].originalData = data;
    }

    private filterComboItem(name: string, info?: ISearchParamItemsSource): JsonObject[] {
        if (!info?.originalData) return [];
        const foreingKeys = info.foreingKeys;
        const refSearchParamPropertyNames = info.refSearchParamPropertyNames;
        if (
            !Array.isArray(foreingKeys) ||
            !foreingKeys.length ||
            !Array.isArray(refSearchParamPropertyNames) ||
            !refSearchParamPropertyNames.length ||
            foreingKeys?.length !== refSearchParamPropertyNames?.length
        )
            return info.originalData;
        let filtered: JsonObject[] = info.originalData;
        for (let i = 0; i < foreingKeys.length; i++) {
            const ref = this._searchParamList.find((param) => {
                return param.property.name === refSearchParamPropertyNames[i];
            });
            if (!ref) continue;
            this._refParams[ref.property.name] ??= [];
            if (!this._refParams[ref.property.name].includes(name)) {
                this._refParams[ref.property.name].push(name);
            }
            const refValue = ref.property.value;
            if (!refValue || (Array.isArray(refValue) && refValue.length === 0)) continue;

            filtered = filtered.filter((item) => {
                const compareTo = item[foreingKeys[i]] ?? (item.jsonData as JsonObject)[foreingKeys[i]] ?? undefined;
                if (!compareTo) return false;
                if (typeof compareTo === 'string' && typeof refValue === 'string') {
                    return refValue === compareTo;
                }
                if (typeof compareTo === 'string' && Array.isArray(refValue)) {
                    return refValue.includes(compareTo);
                }
                if (isStringArray(compareTo) && typeof refValue === 'string') {
                    return compareTo.includes(refValue);
                }
                if (isStringArray(compareTo) && isStringArray(refValue)) {
                    return refValue.some((v) => compareTo.includes(v));
                }
                return false;
            });
        }
        return filtered;
    }

    filterAllComboItems() {
        Object.entries(this.dataItemsSource).forEach(([id, value]) => {
            value.data = this.filterComboItem(id, value);
        });
    }

    removeMissingDefaultValues() {
        this._searchParamList
            .filter((param) => param.active && param.property.itemsSourceInfo)
            .forEach((param) => {
                const itemsSourceInfo = param.property.itemsSourceInfo;
                const data = itemsSourceInfo?.data;
                const defaultValue = param.property.defaultValue;
                if (defaultValue === undefined || defaultValue === null || !data) return;
                //se c'e data di itemSourceInfoi sono per forza in una select o multiselect
                if (typeof defaultValue === 'string') {
                    param.property.defaultValue = data.some((d) => d.id === defaultValue) ? defaultValue : '';
                } else if (Array.isArray(defaultValue)) {
                    param.property.defaultValue = defaultValue.filter((v) => data.some((d) => d.id === v));
                }
            });
    }

    emptyValues(names: string[]) {
        names.forEach((name) => {
            this.params[name] = null;
        });
    }

    constructor(private readonly _searchParamList: SearchParam[]) {
        // Raggruppo i parametri per righe
        const _initialSearchParamsGroupedByRow = groupBy(this._searchParamList, 'layout.row');
        // Rimuovo quelli disabilitati
        this._searchParamList = filter(this._searchParamList, (p) => p.active !== false);
        // Li ordino sulla base della posizione
        this._searchParamList = sortBy(this._searchParamList, ['layout.row', 'layout.column']);
        // Raggruppo di nuovo i parametri ripuliti da quelli disabilitati e ordinati, in righe
        this._searchParamsGroupedByRow = groupBy(this._searchParamList, 'layout.row');

        // Sistemo lo stretch per quei controlli rimasti soli sulle righe in cui
        // i vicini sono stati disabilitati tramite setting
        for (const row in this._searchParamsGroupedByRow) {
            const items = this._searchParamsGroupedByRow[row];
            let isAllSpaceOccupied = false;

            // Stretch solo se c'è qualche parametro disabilitato e ne rimane uno o piu' sulla riga,
            // altrimenti lascio il layout originale scelto
            const originalRowParamsCount = _initialSearchParamsGroupedByRow[row].length;
            if (originalRowParamsCount > 1) {
                for (const element of items) {
                    if (element.layout && element.layout.stretch) {
                        isAllSpaceOccupied = true;
                        break;
                    }
                }

                if (!isAllSpaceOccupied && items[0].layout) {
                    items[0].layout.stretch = true;
                }
            }
        }

        this.params = {};
        this.layouts = {};

        // Popolo il dizionario e aggiungo le proprietà
        forEach(this._searchParamList, (param: SearchParam) => {
            // Se non valorizzato assegno il default
            if (!param.property.value && param.property.defaultValue) {
                param.property.value = param.property.value ?? param.property.defaultValue; //?? per evitare comportamenti indesiderati sui nullish 0 e ''
            }

            // Popolo i dizionari interni
            /* nome - valore */
            if (!this.dataValues[param.property.name]) {
                this.dataValues[param.property.name] = param.property.value;
            }

            /* nome - tipo */
            if (!this.dataType[param.property.name] && param.property.type) {
                this.dataType[param.property.name] = param.property.type;
            }

            /* nome - posizione */
            if (!this.dataLayout[param.property.name] && param.layout) {
                this.dataLayout[param.property.name] = param.layout;
            }

            /* nome - itemsSource */
            if (!!param.property.itemsSourceInfo && !this.dataItemsSource[param.property.name]) {
                if (!param.property.itemsSourceInfo.displayProperty) {
                    param.property.itemsSourceInfo.displayProperty = 'description';
                }
                param.property.itemsSourceInfo.dataLoading = true;
                this.dataItemsSource[param.property.name] = param.property.itemsSourceInfo;
            }

            // Aggiungo le proprietà dinamicamente
            Object.defineProperty(this.params, param.property.name, {
                get: () => {
                    return this.dataValues[param.property.name];
                },
                set: (value: any) => {
                    const oldValue = this.dataValues[param.property.name];
                    let difference = oldValue !== value;
                    if (param.property.type === SearchParamType.fromTo) {
                        /*nel fromTo l'oggetto rimane il solito quindi confrontare la referenze non funziona
                         * bisogna entrare nelle prop e confrontare from e to*/
                        difference = oldValue?.from !== value?.from || oldValue?.to !== value?.to;
                    }
                    if (difference) {
                        this.dataValues[param.property.name] = value;
                        param.property.value = value;
                        // se c'è un nuovo valore filtro tutte le combo.
                        this.filterAllComboItems();
                        // se sto cambiando un valore di ref svuoto tutte le combo che ne fanno riferimento
                        if (this._refParams[param.property.name]?.length) {
                            this.emptyValues(this._refParams[param.property.name]);
                        }
                    }
                },
            });

            // Aggiungo i layout dinamicamente
            Object.defineProperty(this.layouts, param.property.name, {
                get: () => {
                    return this.dataLayout[param.property.name];
                },
            });
        });
    }

    public searchParamsIsAutoOpen(): boolean {
        const valuedParams = this._searchParamList.filter((param) => {
            if (param.property.name === 'date') return false;
            if (Array.isArray(this.params[param.property.name]) && !this.params[param.property.name].length)
                return false;
            return ![undefined, null, ''].includes(this.params[param.property.name]);
        });

        return valuedParams.reduce((acc, currentParam) => {
            return acc && Boolean(currentParam.autoOpen);
        }, true);
    }

    matches(property: string, other: any) {
        const param = this._searchParamList.find((p) => p.property.name === property);
        const value = this.dataValues[property];

        // Un filtro vuoto matcha sempre un valore.
        if (value === null || value === undefined) {
            return true;
        }

        switch (param?.property.type) {
            case SearchParamType.fromTo: {
                // Il fromTo matcha se l'orario passato è incluso nell'intervallo
                if (other.from && other.to) {
                    return rangesOverlap(value, other);
                } else {
                    return isDateWithinRange(value, other);
                }
            }
            case SearchParamType.date:
                // Due date matchano uguali se indicano lo stesso giorno.
                return moment(value).format('dd/MM/yyyy') === moment(other).format('dd/MM/yyyy');
            case SearchParamType.time:
                // Due orari matchano se indicano lo stesso minuto.
                return moment(value).format('hh:mm') === moment(other).format('hh:mm');
            case SearchParamType.multiSelect:
            case SearchParamType.filterMultiSelect:
                // Due multiselect matchano se il primo contiene tutti gli elementi del secondo
                return difference(other, value).length === 0;
            default:
                return value === other;
        }
    }

    toJSON() {
        return this._searchParamList
            .filter((param) => !!this.dataValues[param.property.name])
            .reduce(
                (acc, param) => {
                    switch (param.property.type) {
                        case SearchParamType.time:
                        case SearchParamType.date:
                            acc[param.property.name] = this.serializeDateTime(param);
                            break;
                        case SearchParamType.fromTo:
                            acc[param.property.name] = this.serializeFromTo(param);
                            break;
                        default:
                            acc[param.property.name] = this.dataValues[param.property.name];
                            break;
                    }

                    return acc;
                },
                {} as Record<string, unknown>,
            );
    }

    private resetParam = (param: SearchParam) => {
        if (param.property.defaultValue) {
            if (param.property.type != SearchParamType.fromTo) {
                param.property.value = cloneDeep(param.property.defaultValue);
            } else {
                let defaultValue = cloneDeep(param.property.defaultValue);
                param.property.value = this.parseFromToDefaultValue(defaultValue);
            }
        } else {
            param.property.value = this.getDefaultFieldValue(param);
        }

        this.dataValues[param.property.name] = param.property.value;
    };

    resetSearchParamToDefault() {
        // Popolo il dizionario e aggiungo le proprietà
        forEach(this._searchParamList, this.resetParam);
    }

    clearSearchParamsOnBack() {
        this._searchParamList.filter((param) => !!param.clearOnBack).forEach(this.resetParam);
    }

    private parseFromToDefaultValue(value: { from: string | Date | undefined; to: string | Date | undefined }) {
        if (typeof value.to === 'string') {
            if (value.to.indexOf('#') !== -1) {
                try {
                    let toDMY = (<string>value.to).split('#');
                    let toD = +toDMY[0];
                    let toM = +toDMY[1];
                    let toY = +toDMY[2];

                    value.to = new Date(
                        new Date().getFullYear() + toY,
                        new Date().getMonth() + toM,
                        new Date().getDate() + toD,
                    );
                } catch {
                    value.to = new Date();
                }
            }
        }
        if (typeof value.from === 'string') {
            if (value.from.indexOf('#') !== -1) {
                try {
                    let fromDMY = (<string>value.from).split('#');
                    let fromD = +fromDMY[0];
                    let fromM = +fromDMY[1];
                    let fromY = +fromDMY[2];

                    value.from = new Date(
                        new Date().getFullYear() + fromY,
                        new Date().getMonth() + fromM,
                        new Date().getDate() + fromD,
                    );
                } catch {
                    value.from = new Date();
                }
            }
        }

        return value;
    }

    private getDefaultFieldValue(param: SearchParam) {
        switch (param.property.type) {
            case SearchParamType.boolean:
                return false;
            case SearchParamType.fromTo:
                return { from: undefined, to: undefined };
            case SearchParamType.string:
                return '';
            case SearchParamType.date:
            case SearchParamType.time:
            case SearchParamType.multiSelect:
            case SearchParamType.select:
            case SearchParamType.number:
            default:
                return null;
        }
    }

    private serializeDateTime(param: SearchParam) {
        let value = this.dataValues[param.property.name];
        // Aggiusta fuso
        if (moment.isMoment(value)) {
            value = (this.dataValues[param.property.name] as Moment).toDate();
        }
        if (value instanceof Date) {
            value = value.toJSON();
        }
        return value;
    }

    private serializeFromTo(param: SearchParam) {
        const from = this.dataValues[param.property.name].from;
        const to = this.dataValues[param.property.name].to;
        /*e se sono falsy entrambi non ritorna, quindi al server non arriva nemmeno l'oggetto con from e to undefined
         * da verificare meglio!!!!!*/
        if (from || to) {
            return {
                from: from && from.toJSON ? from.toJSON() : from || undefined,
                to: to && to.toJSON ? to.toJSON() : to || undefined,
            };
        }
    }
}

export class SearchGridConfiguration {
    factoryColumnDefs: EstensaColDef[];
    factoryColState: ColumnState[] = [];
    colState: ColumnState[] = [];
    actualColState: ColumnState[] | null = [];

    constructor(factoryColumnDefs: EstensaColDef[], gridState: GridState) {
        if (gridState.colState) {
            this.colState = gridState.colState;
        }
        this.factoryColumnDefs = factoryColumnDefs;
    }
}

export interface EstensaGridSetting {
    details: SettingDetail[];
    colsState: ColumnState[];
}

export interface SettingDetail {
    colId: string;
    estensa: EstensaColDetails;
    hide: boolean;
}

// DGS_AP
export interface EstensaColDef<TData = any, TValue = any> extends ColDef<TData, TValue> {
    estensaDetails?: EstensaColDetails;
}

export interface EstensaColDetails {
    primary?: boolean;
    valueGetter?: (ValueGetterParams: any) => string | null;
    valueFormatter?: (ValueFormatterParams: any) => string | null;
}

// #region Toolbar

export interface ToolbarState extends ToolbarButtonData {}

export interface ToolbarDef extends ToolbarButtonData {}

export interface SearchToolbarState {
    toolbarState: any;
}

export class SearchToolbarConfiguration {
    factoryToolbarDefs: ToolbarDef[];
    factoryToolbarState: ToolbarState[] = [];
    toolbarState: ToolbarState[] = [];
    actualToolbarState: ToolbarState[] = [];

    constructor(factoryToolbarDefs: ToolbarDef[], searchToolbarState: SearchToolbarState) {
        if (searchToolbarState.toolbarState) {
            this.toolbarState = searchToolbarState.toolbarState;
        }
        this.factoryToolbarDefs = factoryToolbarDefs;
    }
}

// end #region
